// DOC-LINKS (WIKI-IN-COMPANYOS): the links between CompanyOS docs, read from the doc itself, so the
// Related tab works with memory off and renames never break a link. Pure: the API stores what this
// returns in `cell.page_link` on every save and reads tags off the text.
//
// Link markup in a page body (BlockNote JSON), the one contract the editor, agents and imports
// write and this module reads:
//  1. A BlockNote `link` inline whose href is a doc's address, `…/c/{slug}/work/p/{uuid}` (any
//     origin or none, as `pageLink` writes it): an id link. Its text is the label at save time;
//     the title shown is the target's live title.
//  2. `[[Title|uuid]]` in the text: an id link written as text. The editor's [[ picker inserts it
//     as a `pageLink` inline node (`pageLinkInline`), which markdown writes back as this text.
//  3. `[[Title]]` in the text: a title link. The API resolves it by title when the page is saved
//     (same space first, case-insensitive); unresolved, it is stored with its raw title and no id,
//     and resolves when a page with that title is created or renamed to it.
// `#tags` in the text are tags (a tag starts with a letter, so `#123`, a task number, is not one).

export type PageLinkOut = {
  /** The linked doc's id; null for a `[[Title]]` link the caller still has to resolve. */
  target_page_id: string | null;
  /** The raw text as written: the `[[Title]]` text or the link's label. */
  target_title: string;
  /** The text of the block the link sits in, whitespace folded. */
  context: string;
};

const UUID_RE = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const DOC_HREF = new RegExp(`/work/p/(${UUID_RE})(?![0-9a-f-])`, "i");
const WIKI = new RegExp(`\\[\\[([^\\[\\]|]+?)(?:\\|(${UUID_RE}))?\\]\\]`, "gi");
export const LINK_TITLE_MAX = 200;
export const LINK_CONTEXT_MAX = 300;
/** A bound on the work: a doc names at most this many other docs in its links. */
export const MAX_PAGE_LINKS = 200;

const fold = (s: string, max: number) => s.replace(/\s+/g, " ").trim().slice(0, max);

/**
 * WIKI-LINKS-GRAPH: the [[ picker's inline, `{type: "pageLink", props: {pageId, title}}`: an id link
 * (form 2, `[[Title|uuid]]`, as an editor node) whose `title` is the title when it was picked; the
 * editor shows the live one. Its text is the title; markdown writes it as `[[Title|uuid]]`.
 */
export function pageLinkInline(o: unknown): { pageId: string; title: string } | null {
  if (!o || typeof o !== "object") return null;
  const n = o as { type?: unknown; props?: unknown };
  if (n.type !== "pageLink" || !n.props || typeof n.props !== "object") return null;
  const p = n.props as { pageId?: unknown; title?: unknown };
  if (typeof p.pageId !== "string" || !new RegExp(`^${UUID_RE}$`, "i").test(p.pageId)) return null;
  return { pageId: p.pageId.toLowerCase(), title: typeof p.title === "string" ? p.title : "" };
}

/**
 * A `pageLink` with no id (the editor shows a `[[Title]]` written as text this way): its text is
 * the `[[Title]]` it stands for, so it reads, resolves and exports as a title link.
 */
export function titleLinkText(o: unknown): string {
  const p = (o as { props?: { title?: unknown; pageId?: unknown } } | null)?.props;
  if (typeof p?.pageId === "string" && p.pageId !== "") return "";
  const t = typeof p?.title === "string" ? p.title.replace(/[[\]|]/g, " ").trim() : "";
  return t ? `[[${t}]]` : "";
}

/** The plain text of an inline array (or a string), nested content and table cells included. */
function textOf(content: unknown): string {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) return content.map(textOf).join("");
  if (!content || typeof content !== "object") return "";
  const o = content as {
    type?: unknown;
    text?: unknown;
    content?: unknown;
    rows?: unknown;
    cells?: unknown;
    props?: unknown;
  };
  if (typeof o.text === "string") return o.text;
  if (o.type === "pageLink") return pageLinkInline(o)?.title ?? titleLinkText(o);
  if (Array.isArray(o.rows)) return o.rows.map(textOf).join(" ");
  if (Array.isArray(o.cells)) return o.cells.map(textOf).join(" ");
  return textOf(o.content);
}

/** Every `link` inline anywhere under `content` (table cells too), in reading order. */
function linksIn(content: unknown, out: { href: string; label: string }[]) {
  if (Array.isArray(content)) {
    for (const c of content) linksIn(c, out);
    return;
  }
  if (!content || typeof content !== "object") return;
  const o = content as { type?: unknown; href?: unknown; content?: unknown };
  if (o.type === "link" && typeof o.href === "string") {
    out.push({ href: o.href, label: textOf(o.content) });
    return;
  }
  if (o.type === "pageLink") {
    const p = pageLinkInline(o);
    if (p) out.push({ href: `/work/p/${p.pageId}`, label: p.title });
    return;
  }
  for (const v of Object.values(content)) if (v && typeof v === "object") linksIn(v, out);
}

/** The doc id an href points at, when it is a CompanyOS doc address; lower case. */
export function docIdOfHref(href: string): string | null {
  const m = DOC_HREF.exec(href);
  return m?.[1] ? m[1].toLowerCase() : null;
}

/** The `[[Title]]` / `[[Title|uuid]]` links in a text, in order. */
export function wikiLinksOf(text: string): { title: string; id: string | null }[] {
  return [...text.matchAll(WIKI)].map((m) => ({
    title: fold(m[1] ?? "", LINK_TITLE_MAX),
    id: m[2] ? m[2].toLowerCase() : null,
  }));
}

/** The dedupe key of a link: its id, else its title (case-insensitive). */
export const linkKey = (l: { target_page_id: string | null; target_title: string }) =>
  l.target_page_id ? `id:${l.target_page_id}` : `t:${l.target_title.toLowerCase()}`;

/**
 * The links of a BlockNote document to CompanyOS docs, in reading order: one per target (an id
 * once, a title once), never to `selfId`, at most `MAX_PAGE_LINKS`.
 */
export function pageLinksOf(blocks: readonly unknown[], selfId: string): PageLinkOut[] {
  const out: PageLinkOut[] = [];
  const seen = new Set<string>([`id:${selfId.toLowerCase()}`]);
  const add = (l: PageLinkOut) => {
    const k = linkKey(l);
    if (seen.has(k) || (l.target_page_id === null && l.target_title === "")) return;
    seen.add(k);
    out.push(l);
  };
  const walk = (list: readonly unknown[]) => {
    for (const b of list) {
      if (out.length >= MAX_PAGE_LINKS) return;
      if (!b || typeof b !== "object") continue;
      const o = b as { content?: unknown; children?: unknown };
      const text = textOf(o.content);
      const context = fold(text, LINK_CONTEXT_MAX);
      const found: { href: string; label: string }[] = [];
      linksIn(o.content, found);
      for (const l of found) {
        const id = docIdOfHref(l.href);
        if (id) add({ target_page_id: id, target_title: fold(l.label, LINK_TITLE_MAX), context });
      }
      for (const w of wikiLinksOf(text))
        add({ target_page_id: w.id, target_title: w.title, context });
      if (Array.isArray(o.children)) walk(o.children);
    }
  };
  walk(blocks);
  return out.slice(0, MAX_PAGE_LINKS);
}

// A tag starts with a letter (so `#123`, a task number, is not one), after a space or the start.
const TAG = /(?:^|[\s(])#([\p{L}][\p{L}\p{N}_/-]*)/gu;

/** The `#tags` in a doc's text, lower case, first seen first, at most `max`. */
export function tagsOf(text: string, max = 40): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const m of text.matchAll(TAG)) {
    const t = (m[1] ?? "").replace(/[/-]+$/, "").toLowerCase();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
    if (out.length >= max) break;
  }
  return out;
}
